import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';
import { useSignin } from '../hooks/useAuth';
import { authService } from '../../../services/authService';
import { useAuthStore } from '../../../stores/authStore';
import { showToast } from '../../../stores/uiStore';
import { GoogleLoginButton } from './GoogleLoginButton';
import { handleApiError } from '../../../services/api';
import { ROUTES } from '../../../config/routes';
import { getOperationErrorMessage } from '../../../config/errorMessages';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';

const loginSchema = z.object({
  email: z.string().email('有効なメールアドレスを入力してください'),
  password: z.string().min(1, 'パスワードを入力してください'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [showPassword, setShowPassword] = React.useState(false);
  const [mfaChallenge, setMfaChallenge] = React.useState<{
    session: string;
    email: string;
  } | null>(null);
  const [mfaCode, setMfaCode] = React.useState('');
  const [mfaError, setMfaError] = React.useState('');
  const [mfaSubmitting, setMfaSubmitting] = React.useState(false);
  const [isRateLimited, setIsRateLimited] = React.useState(false);

  const { mutateAsync: signin, isPending } = useSignin();

  // stashed emailをdefaultValueに設定（SessionExpiredModal経由のprefill）
  const stashedEmail = sessionStorage.getItem('eb735_login_email') || '';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: stashedEmail },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      const response = await signin(data);
      if (response.success && response.data) {
        const data = response.data as any;
        if (data.challenge?.challenge_name === 'SOFTWARE_TOKEN_MFA') {
          setMfaChallenge({
            session: data.challenge.session,
            email: data.challenge.email,
          });
        } else if (data.user && data.tokens) {
          const { user, tokens } = data;
          setAuth(user, tokens);
          sessionStorage.removeItem('eb735_login_email');
          showToast.success('ログインしました');
          navigate(ROUTES.HOME);
        }
      }
    } catch (error) {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
      if (apiError.code === 'RATE_LIMIT_EXCEEDED') {
        const axiosErr = error as any;
        const retryAfter = axiosErr?.response?.data?.retry_after ?? 300;
        setIsRateLimited(true);
        setTimeout(() => setIsRateLimited(false), retryAfter * 1000);
      }
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaChallenge) return;
    setMfaError('');
    setMfaSubmitting(true);
    try {
      const response = await authService.mfaVerify({
        session: mfaChallenge.session,
        code: mfaCode,
        email: mfaChallenge.email,
      });
      if (response.success && response.data) {
        const { user, tokens } = response.data;
        setAuth(user, tokens);
        sessionStorage.removeItem('eb735_login_email');
        showToast.success('ログインしました');
        navigate(ROUTES.HOME);
      } else {
        setMfaError(response.error?.message || getOperationErrorMessage('auth'));
      }
    } catch (error) {
      const apiError = handleApiError(error);
      setMfaError(apiError.message);
    } finally {
      setMfaSubmitting(false);
    }
  };

  if (mfaChallenge) {
    return (
      <form onSubmit={handleMfaSubmit} noValidate className="w-full">
        <h1 className="text-2xl font-semibold mb-1">MFA認証</h1>
        <p className="text-sm text-text-secondary mb-6">
          認証アプリに表示された6桁のコードを入力してください
        </p>

        <div className="mb-4">
          <label htmlFor="login-mfa-code" className="block text-sm font-medium mb-1.5">認証コード</label>
          <Input
            id="login-mfa-code"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            placeholder="000000"
            maxLength={6}
            inputMode="numeric"
            pattern="[0-9]*"
            autoFocus
          />
          {mfaError && (
            <p className="text-sm text-red-500 mt-1">{mfaError}</p>
          )}
        </div>

        <Button
          type="submit"
          className="w-full h-10 mt-2"
          disabled={mfaSubmitting || mfaCode.length !== 6}
        >
          {mfaSubmitting ? '認証中...' : '認証'}
        </Button>

        <div className="text-center mt-4">
          <button
            type="button"
            className="text-sm text-primary hover:underline"
            onClick={() => setMfaChallenge(null)}
          >
            ログイン画面に戻る
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="w-full">
      <div className="mb-4 md:mb-5">
        <label htmlFor="login-email" className="block text-sm font-medium mb-1.5">メールアドレス</label>
        <Input
          {...register('email')}
          id="login-email"
          type="email"
          placeholder="you@example.com"
          autoComplete="email"
          className="text-base md:text-sm"
          aria-invalid={!!errors.email}
          aria-describedby="login-email-error"
          data-testid="login-email-input"
        />
        {errors.email && (
          <p id="login-email-error" role="alert" className="text-sm text-red-500 mt-1">
            {errors.email.message}
          </p>
        )}
      </div>

      <div className="mb-4 md:mb-5">
        <label htmlFor="login-password" className="block text-sm font-medium mb-1.5">パスワード</label>
        <div className="relative">
          <Input
            {...register('password')}
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            placeholder="パスワードを入力"
            autoComplete="current-password"
            className="pr-10 text-base md:text-sm"
            aria-invalid={!!errors.password}
            aria-describedby="login-password-error"
            data-testid="login-password-input"
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-text-primary"
            onClick={() => setShowPassword(!showPassword)}
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
        {errors.password && (
          <p id="login-password-error" role="alert" className="text-sm text-red-500 mt-1">
            {errors.password.message}
          </p>
        )}
      </div>

      <div style={{ textAlign: 'right', marginTop: '6px', marginBottom: '20px' }}>
        <RouterLink
          to={ROUTES.FORGOT_PASSWORD}
          style={{ fontSize: '14px', color: '#0a7a72' }}
        >
          パスワードをお忘れですか？
        </RouterLink>
      </div>

      <Button
        type="submit"
        className="w-full py-3.5 md:py-3 rounded-lg text-base md:text-[15px] font-bold hover:bg-primary-dark transition-colors"
        disabled={isPending || isRateLimited}
        data-testid="login-submit-button"
      >
        {isPending ? 'ログイン中...' : isRateLimited ? 'しばらくお待ちください...' : 'ログイン'}
      </Button>

      <div className="flex items-center gap-4 my-6">
        <div className="flex-1 h-px bg-border"></div>
        <span className="text-xs text-gray-500">または</span>
        <div className="flex-1 h-px bg-border"></div>
      </div>

      <GoogleLoginButton mode="login" testId="google-login-button" />

      <div className="text-center mt-8">
        <p className="text-sm text-text-secondary">
          アカウントをお持ちでない方は{' '}
          <RouterLink
            to={ROUTES.SIGNUP}
            className="text-sm text-primary font-semibold hover:underline"
          >
            新規登録
          </RouterLink>
        </p>
      </div>
    </form>
  );
};
